import { FlexRow, MaterialIcon, MaterialIconName } from '@gorila-shared-ui/components';
import { LabelXSmall } from 'baseui/typography';
import { memo } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { OperationalState, OperationalStateStatus } from '../../types/currentState';
import SleepIcon from '../icons/SleepIcon';

type Props = {
  operationalState: OperationalState;
  speed?: number;
  positionDirection?: number;
  fillWidth?: boolean;
  withVerticalWhiteSpace?: boolean;
  sleepMode?: boolean;
};

function OperationalStatusLabel({
  operationalState,
  speed,
  positionDirection = 0,
  fillWidth = false,
  withVerticalWhiteSpace = false,
  sleepMode = false,
}: Readonly<Props>) {
  const { css, theme } = useStyles();
  const wrapperStyles = css({
    padding: `${withVerticalWhiteSpace ? theme.sizing.scale200 : theme.sizing.scale100} ${theme.sizing.scale300}`,
    background: theme.colors.backgroundSecondary,
    borderRadius: theme.borders.radius200,
    width: fillWidth ? '100%' : 'auto',
  });
  const wrapperProps = {
    classNames: wrapperStyles,
    gap: theme.sizing.scale100,
    centered: true,
  };

  if (!operationalState.currentStatus) {
    return null;
  }

  const isMoving = operationalState.currentStatus === OperationalStateStatus.movement && speed && speed > 0;

  const sleepModeLabel = sleepMode ? (
    <span style={{ paddingLeft: '2px', display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
      <SleepIcon size={14} /> Sleep
    </span>
  ) : null;

  if (isMoving) {
    return (
      <FlexRow {...wrapperProps}>
        <span
          className={css({
            transform: `rotate(${positionDirection}deg)`,
            transition: 'transform 0.3s ease',
            display: 'inline-flex',
          })}
        >
          <MaterialIcon
            name="navigation"
            success
            size="16px"
          />
        </span>
        <LabelXSmall
          color={theme.colors.contentSecondary}
          style={{ whiteSpace: 'nowrap', display: 'inline-flex' }}
        >
          {operationalState.currentStatus} {sleepModeLabel}
        </LabelXSmall>
      </FlexRow>
    );
  }

  const iconName: MaterialIconName = (() => {
    if (operationalState.currentStatus === OperationalStateStatus.inactive) return 'do_not_disturb_on';
    if (operationalState.currentStatus === OperationalStateStatus.movement) return 'navigation';
    if (operationalState.currentStatus === OperationalStateStatus.ralenti) return 'initial_speed';
    return 'power_settings_new';
  })();

  const iconColor = (() => {
    if (operationalState.currentStatus === OperationalStateStatus.on) return theme.colors.positive;
    if (operationalState.currentStatus === OperationalStateStatus.off) return theme.colors.negative;
    return theme.colors.contentSecondary;
  })();

  return (
    <FlexRow {...wrapperProps}>
      <MaterialIcon
        name={iconName}
        color={iconColor}
        size="16px"
      />
      <LabelXSmall
        color={theme.colors.contentSecondary}
        style={{ whiteSpace: 'nowrap', display: 'inline-flex' }}
      >
        {operationalState.currentStatus} {sleepModeLabel}
      </LabelXSmall>
    </FlexRow>
  );
}

export default memo(OperationalStatusLabel);
